import React, { useEffect, useState } from 'react';

import moment from 'moment';
import { useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { get_api_url as getApiUrl } from '../../../utils/urls';
import { get_api as getApi } from '../../../redux/api_funcs';
import CustomDatePicker from '../../util_components/custom_date_picker';
import * as actions from '../../../redux/action_creators';
import CustomSingleSelect from '../../util_components/custom_single_select';
import CustomLoading from '../../util_components/custom_loading';
import Paginator from '../../util_components/paginator';
import { teacherFullNameOf } from '../../../utils/teacher_name';
import { downloadCsv } from '../../../utils/csv';
import {
  CATEGORIES,
  QA_PAGE_SIZE,
  QA_PROFILE_TEACHER_LIMIT,
  QA_CSV_HEADERS,
  buildQaCsvRows,
  buildQaScoresQuery,
  formatSessionDate,
  lookupScore,
  normalizeScoreRow,
  rowTotal
} from './qa_score_utils';

const QA_SCORES_PATH = '/v2/crons-tasks/poc/qascores';

function GptSessionQaScores() {
  const [scores, setQaScores] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [avgScore, setAvgScore] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [truncated, setTruncated] = useState(false);

  const [startDate, setStartDate] = useState(moment().subtract(1, 'days').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const psUUID = queryParams.get('student');
  const ptUUID = queryParams.get('teacher');
  const isFilterMode = psUUID == null && ptUUID == null;

  const dispatch = useDispatch();
  const teachersList = useSelector((state) => state.teachers.teachers_list);
  const ianaTimezone = useSelector((state) => state.home.iana_timezone);
  const [teacherSelectionList, setTeacherSelectionList] = useState([]);
  const [filterTeacher, setFilterTeacher] = useState('all');
  // Filters the list was loaded with; Submit and teacher change replace it, date edits alone do not.
  const [applied, setApplied] = useState({ teacher: 'all', since: startDate, till: endDate });
  const [page, setPage] = useState(1);

  const handleDateChange = (type, value) => {
    if (type === 'start_date') {
      setStartDate(value);
    } else if (type === 'end_date') {
      setEndDate(value);
    }
  };

  const queryFor = (extra) =>
    buildQaScoresQuery({
      studentUuid: psUUID,
      profileTeacherUuid: ptUUID,
      teacher: applied.teacher,
      since: applied.since,
      till: applied.till,
      timezone: ianaTimezone,
      ...extra
    });

  // Prefers the backend's reason so bad-filter 400s don't read as outages.
  const notifyError = (err, fallback) =>
    dispatch(
      actions.set_notification_variable(true, 'error', err?.response?.data?.reason || fallback)
    );

  useEffect(() => {
    dispatch(actions.get_teachers_list());
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const url = getApiUrl(`${QA_SCORES_PATH}?${queryFor({ page })}`, null, true);
    getApi(url, true)
      .then((result) => {
        if (cancelled) return;
        const data = result?.data?.data || {};
        setQaScores((data.rows || []).map(normalizeScoreRow));
        setTotalCount(data.total_count || 0);
        setAvgScore(data.avg_total_score || 0);
      })
      .catch((err) => {
        if (!cancelled) notifyError(err, 'Failed to load QA scores. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied, page, ianaTimezone, psUUID, ptUUID]);

  useEffect(() => {
    const allTeachers = teachersList.filter((t) => t.onboarding_status === 'APPROVED');
    const lteachers = allTeachers.map((teacher) => ({
      value: teacher.uuid,
      label: teacherFullNameOf(teacher)
    }));
    lteachers.sort((a, b) => a.label.localeCompare(b.label));
    lteachers.unshift({ value: 'all', label: 'All' });
    setTeacherSelectionList(lteachers);
  }, [teachersList]);

  const applyFilters = (teacher) => {
    setApplied({ teacher, since: startDate, till: endDate });
    setPage(1);
    setTruncated(false);
  };

  const handleTeacherChange = (e) => {
    const { value } = e.target;
    setFilterTeacher(value);
    applyFilters(value);
  };

  const handleSubmit = () => {
    applyFilters(filterTeacher);
  };

  const onDownloadCsv = () => {
    setExporting(true);
    setTruncated(false);
    const url = getApiUrl(`${QA_SCORES_PATH}?${queryFor({ exportAll: true })}`, null, true);
    getApi(url, true)
      .then((result) => {
        const data = result?.data?.data || {};
        const teacherPart = applied.teacher !== 'all' ? `-${applied.teacher}` : '';
        downloadCsv(
          `qa-scores-${applied.since}-to-${applied.till}${teacherPart}.csv`,
          QA_CSV_HEADERS,
          buildQaCsvRows((data.rows || []).map(normalizeScoreRow), ianaTimezone)
        );
        setTruncated(!!data.truncated);
      })
      .catch((err) => notifyError(err, 'Failed to export QA scores. Please try again.'))
      .finally(() => setExporting(false));
  };

  const totalPages = Math.ceil(totalCount / QA_PAGE_SIZE);
  const firstRow = (page - 1) * QA_PAGE_SIZE + 1;
  const lastRow = firstRow + scores.length - 1;
  // Profile links show a fixed slice, so their average covers only the rows on screen.
  const displayedAvg = isFilterMode
    ? avgScore
    : scores.reduce((sum, item) => sum + item.total_score, 0) / (scores.length || 1);

  const renderSummary = () => {
    if (ptUUID != null)
      return `* Latest ${QA_PROFILE_TEACHER_LIMIT} sessions are shown for this teacher`;
    if (isFilterMode && !loading && totalCount > 0)
      return `Showing ${firstRow}–${lastRow} of ${totalCount} sessions`;
    return null;
  };

  return (
    <div className="common-page-container h-full">
      <div className="font-bold text-black text-[30px] mb-5">QA Scores</div>

      {isFilterMode && (
        <div className="flex flex-col gap-5">
          <p className="">Select a Teacher or Date</p>

          <div className="flex items-center gap-5">
            <div className="daily-report-item">
              <CustomSingleSelect
                labelClass="hidden"
                label=""
                options={teacherSelectionList}
                name="filter_teacher"
                onChange={handleTeacherChange}
                value={filterTeacher}
                style={{ width: '250px' }}
              />
            </div>

            <div className="daily-report-item">
              <CustomDatePicker
                label="Start Date"
                value={startDate}
                handleChange={(val) => handleDateChange('start_date', val)}
                maxDate={endDate}
              />
            </div>
            <div className="daily-report-item">
              <CustomDatePicker
                label="End Date"
                value={endDate}
                handleChange={(val) => handleDateChange('end_date', val)}
                minDate={startDate}
                maxDate={moment().format('YYYY-MM-DD')}
              />
            </div>
            <button className="mt-[25px]" onClick={handleSubmit} type="button">
              Submit
            </button>
            <button
              className="mt-[25px]"
              onClick={onDownloadCsv}
              type="button"
              disabled={exporting || totalCount === 0}
            >
              {exporting ? 'Exporting…' : 'Download CSV'}
            </button>
          </div>
        </div>
      )}

      {/* A silently short file would read as complete, so say so when the cap was hit. */}
      {truncated ? (
        <div className="mt-2 text-sm" style={{ color: 'var(--state-error)' }}>
          The export hit its row limit, so the downloaded file is incomplete. Narrow the dates and
          download again.
        </div>
      ) : null}
      <div className="mt-5 text-sm">{renderSummary()}</div>
      <div className="mt-2 text-sm">Average Score: {Number(displayedAvg).toFixed(2)}</div>
      <table className="session-table">
        <thead>
          <tr>
            <th className="text-center capitalize" colSpan={5}>
              Association
            </th>
            {CATEGORIES.map((category) => (
              <th
                key={`cat-${category.t}`}
                className="text-center capitalize"
                colSpan={category.p.length}
              >
                {category.t}
              </th>
            ))}
          </tr>
          <tr>
            <th className="text-center capitalize">Session</th>
            <th className="text-center capitalize">Date</th>
            <th className="text-center capitalize">Student</th>
            <th className="text-center capitalize">Coach</th>
            <th className="text-center capitalize">Total</th>
            {CATEGORIES.map((category) =>
              category.p.map((point) => (
                <th
                  key={`cat-${category.t}-${point}-th`}
                  className="text-center capitalize"
                  data-tooltip-id="my-tooltip"
                  data-tooltip-content={point}
                >
                  {point.replaceAll(' ', '\n')}
                </th>
              ))
            )}
          </tr>
        </thead>
        <tbody>
          {!loading && scores.length === 0 && (
            <tr>
              <td
                className="text-center"
                colSpan={5 + CATEGORIES.reduce((n, c) => n + c.p.length, 0)}
              >
                No QA scores for the selected filters
              </td>
            </tr>
          )}
          {scores.map((score) => (
            <tr key={`score-${score.session_uuid}`}>
              <td className="text-center">
                <a
                  href={`/sessiondetails?id=${score.session_uuid}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:text-blue-600 cursor-pointer underline"
                  data-tooltip-id="my-tooltip"
                  data-tooltip-content="Session Details"
                >
                  Session
                </a>
              </td>
              <td className="text-center whitespace-nowrap">
                {formatSessionDate(score.session_start_time, ianaTimezone)}
              </td>
              <td className="text-center">
                <a
                  href={`/studentprofile?id=${score.student_uuid}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-tooltip-id="my-tooltip"
                  className="text-blue-500 hover:text-blue-600 cursor-pointer underline capitalize"
                  data-tooltip-content="Student Profile"
                >
                  {score.student_name}
                </a>
              </td>
              <td className="text-center">
                <a
                  href={`/teacherprofile?id=${score.teacher_uuid}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-tooltip-id="my-tooltip"
                  data-tooltip-content="Coach Profile"
                  className="text-blue-500 hover:text-blue-600 cursor-pointer underline capitalize"
                >
                  {score.teacher_name}
                </a>
              </td>
              <td
                className="center"
                data-tooltip-id="my-tooltip"
                data-tooltip-content="Total Score"
              >
                <span className="text-center">{rowTotal(score)}</span>
              </td>
              {CATEGORIES.map((category) =>
                category.p.map((point) => {
                  const cps = lookupScore(score.qa_score, category.t, point);
                  return (
                    <td
                      key={`cat-${category.t}-${point}`}
                      className="center"
                      data-tooltip-id="my-tooltip"
                      data-tooltip-content={`${category.t}:${point}->${cps.reason}`}
                    >
                      <span className="text-center">{cps.score}</span>
                    </td>
                  );
                })
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {loading && <CustomLoading />}
      {isFilterMode && totalPages > 1 && (
        <Paginator curr_page={page} page_limit={totalPages} pageChange={setPage} />
      )}
    </div>
  );
}

export default GptSessionQaScores;
